import { Company } from '../../types/companies/Companies';
import { doGet, doPost } from '../http/httpHelper';

export interface PatientsV2ListItemResponse {
  id: number | string;
  name: string;
  regulatoryStatus: string;
  requestedAt: string;
}

export interface PatientsV2ListResponse {
  rows: number;
  page: number;
  pageSize: number;
  data: PatientsV2ListItemResponse[];
}

export interface PatientV2DetailResponse {
  id: number | string;
  name: string;
  regulatoryStatus: string;
  operationalStatus?: string;
  sap: string;
  cpf: string;
  birthDate: string;
  email: string;
  cellphone: string;
  cellphone2: string;
  cep: string;
  address: string;
  addressLine2: string;
  neighborhood: string;
  city: string;
  number: string;
  uf: string;
  company: Company;
  onPaymentList: boolean;
  requestCreatedBy?: number | string | null;
  canEdit: boolean;
  history: PatientV2HistoryItemResponse[];
  clinicalInformation: PatientV2ClinicalInformationPayload | null;
  documents: PatientV2DocumentResponse[];
  regulatoryDecision?: PatientV2RegulatoryDecisionResponse | null;
  review: {
    patient: string;
    clinical: string;
    documents: string;
    issues?: PatientV2ReviewIssueResponse[];
    canStartReview?: boolean;
    canMakeRegulatoryDecision?: boolean;
  };
}

export interface PatientV2ReviewIssueResponse {
  section: string;
  field: string;
  label: string;
  description: string;
  documentId?: number | string;
}

export type PatientV2RegulatoryDecision = 'Deferido' | 'Deferido provisório' | 'Indeferimento provisório' | 'Indeferido';

export interface PatientV2RegulatoryDecisionResponse {
  id: number | string;
  decision: PatientV2RegulatoryDecision;
  justification?: string | null;
  decidedBy: number | string;
  decidedByName: string;
  decidedAt: string;
  createdAt: string;
  isCurrent?: boolean;
}

export interface PatientV2RegulatoryDecisionPayload {
  decision: PatientV2RegulatoryDecision;
  justification?: string;
}

export interface PatientV2DocumentResponse {
  id: number | string;
  documentType: string;
  originalFileName: string;
  mimeType: string;
  fileSizeBytes: number;
  documentGeneratedAt?: string | null;
  documentExpiresAt?: string | null;
  uploadedAt?: string | null;
  reviewStatus: string;
  reviewReason?: string;
}

export interface PatientV2DocumentCreatePayload {
  documentType?: string;
  customDocumentType?: string;
  documentGeneratedAt?: string;
  originalFileName: string;
  mimeType: string;
  fileSizeBytes: number;
  contentBase64: string;
}

export interface PatientV2DocumentReplacementPayload extends PatientV2DocumentCreatePayload {
  replacedDocumentId: number | string;
}

export interface PatientV2DocumentContentResponse {
  id: number | string;
  originalFileName: string;
  mimeType: string;
  contentBase64: string;
}

export interface PatientV2ClinicalInformationPayload {
  cidPrimary: string;
  cidSecondary: string;
  clinicalDiagnosis: string;
  oxygenIndication: string;
  prescribedOxygenSource: string;
  oxygenFlowLMin: string;
  dailyUseHours: string;
  requestedEquipment: string;
  requestingDoctorName: string;
  requestingDoctorCrm: string;
  requestDate: string;
  reviewStatus?: string;
  reviewNotes?: string;
}

export interface PatientV2HistoryItemResponse {
  id: number | string;
  eventType: string;
  regulatoryStatus: string;
  decision?: string | null;
  reason?: string | null;
  createdAt: string;
  requestId: string;
}

export interface PatientV2TriageItemResponse {
  id: number | string;
  entryKind: 'workflow' | 'note' | string;
  eventType: string;
  stage: string;
  regulatoryStatus: string;
  decision?: string | null;
  detailsText?: string;
  detailsJson?: unknown;
  createdBy: number | string;
  createdByName: string;
  createdByEmail?: string;
  createdAt: string;
}

export interface PatientV2TriageNotePayload {
  message: string;
  stage?: string;
}

export interface PatientV2CreatePayload {
  patient: string;
  sap: string;
  cpf: string;
  birthDate: string;
  email: string;
  cellphone: string;
  phoneNumber: string;
  cep: string;
  address: string;
  number: string;
  addressLine2: string;
  neighborhood: string;
  city: string;
  uf: string;
  companyId: number;
  clinicalInformation: PatientV2ClinicalInformationPayload;
  documents: PatientV2DocumentCreatePayload[];
}

export interface PatientV2CompanyOption {
  id: number;
  cnpj: string;
  companyName: string;
}

export interface PatientV2SapLookupResponse {
  patient: Partial<PatientV2CreatePayload>;
  companies: PatientV2CompanyOption[];
}

export interface PatientV2CreateResponse {
  id: number | string;
  requestId: string;
  regulatoryStatus: string;
}

export interface PatientV2ResubmitPayload extends Omit<PatientV2CreatePayload, 'documents'> {
  documentReplacements: PatientV2DocumentReplacementPayload[];
}

export interface PatientV2ReviewPendencyPayload {
  field: string;
  label: string;
  comment: string;
}

export interface PatientV2ReviewSectionPayload {
  decision: 'approved' | 'pending';
  pendencies?: PatientV2ReviewPendencyPayload[];
  documentPendencies?: Array<{ documentId: number | string; comment?: string }>;
  documentApprovals?: Array<{ documentId: number | string; documentExpiresAt?: string }>;
}

export interface PatientV2ReviewPayload {
  sections: {
    patient: PatientV2ReviewSectionPayload;
    clinical: PatientV2ReviewSectionPayload;
    documents?: PatientV2ReviewSectionPayload;
  };
}

export interface PatientV2ReviewResponse {
  id: number | string;
  regulatoryStatus: string;
  operationalStatus?: string;
  historyItem: PatientV2HistoryItemResponse;
}

export interface PatientV2RegulatoryDecisionSaveResponse extends PatientV2ReviewResponse {
  regulatoryDecision: PatientV2RegulatoryDecisionResponse;
}

export interface PatientsV2MetricsResponse {
  inProgram: number;
  requested:number;
  pending: number;
  inAnalysis: number;
}

const getErrorMessage = (response: any, fallback: string) => {
  return response?.body?.message ?? response?.body?.body?.message ?? response?.message ?? response?.error ?? response?.body ?? fallback;
}

const getSuccessfulPayload = <T>(response: any, fallback: string): T => {
  if (response?.httpStatus && ![200, 201].includes(response.httpStatus)) {
    throw new Error(getErrorMessage(response, fallback));
  }

  return (response && typeof response === 'object' && 'body' in response ? response.body : response) as T;
};

export const getPatientsV2 = async ({ page, pageSize, search }: { page: number; pageSize: number; search?: string }): Promise<PatientsV2ListResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    refresh: String(Date.now()),
  });
  if (search?.trim()) params.set('search', search.trim());

  const response: any = await doGet(`/patientsv2?${params.toString()}`);
  return getSuccessfulPayload<PatientsV2ListResponse>(response, 'Não foi possível carregar os pacientes.');
};

export const getPatientV2ById = async (patientId: string): Promise<PatientV2DetailResponse> => {
  const response: any = await doGet(`/patientsv2/${encodeURIComponent(patientId)}?refresh=${Date.now()}`);
  if (response?.httpStatus && response.httpStatus !== 200) {
    const error = new Error(getErrorMessage(response, 'Não foi possível carregar o paciente.')) as Error & { status?: number };
    error.status = response?.httpStatus;
    throw error;
  }
  return getSuccessfulPayload<PatientV2DetailResponse>(response, 'Não foi possível carregar o paciente.');
};

export const getPatientV2SapCompanies = async (sap: string): Promise<PatientV2SapLookupResponse> => {
  const response: any = await doGet(`/patientsv2/sap/${encodeURIComponent(sap)}/companies`);
  return getSuccessfulPayload<PatientV2SapLookupResponse>(response, 'Não foi possível validar o código SAP.');
};

export const getPatientV2DocumentContent = async (patientId: string | number, documentId: string | number): Promise<PatientV2DocumentContentResponse> => {
  const response: any = await doGet(`/patientsv2/${encodeURIComponent(String(patientId))}/documents/${encodeURIComponent(String(documentId))}/content?refresh=${Date.now()}`);
  return getSuccessfulPayload<PatientV2DocumentContentResponse>(response, 'Não foi possível abrir o documento.');
};

export const getPatientV2Triage = async (patientId: string | number): Promise<PatientV2TriageItemResponse[]> => {
  const response: any = await doGet(`/patientsv2/${encodeURIComponent(String(patientId))}/triage?refresh=${Date.now()}`);
  return getSuccessfulPayload<PatientV2TriageItemResponse[]>(response, 'Não foi possível carregar a triagem.');
};

export const createPatientV2 = async (payload: PatientV2CreatePayload): Promise<PatientV2CreateResponse> => {
  const response: any = await doPost('/patientsv2', payload);
  if (response?.httpStatus && response.httpStatus !== 201) {
    throw new Error(getErrorMessage(response, 'Não foi possível cadastrar o paciente.'));
  }

  return getSuccessfulPayload<PatientV2CreateResponse>(response, 'Não foi possível cadastrar o paciente.');
};

export const reviewPatientV2 = async (patientId: string | number, payload: PatientV2ReviewPayload): Promise<PatientV2ReviewResponse> => {
  const response: any = await doPost(`/patientsv2/${encodeURIComponent(String(patientId))}/review`, payload);
  if (response?.httpStatus && response.httpStatus !== 200) {
    throw new Error(getErrorMessage(response, 'Não foi possível concluir a análise do paciente.'));
  }

  return getSuccessfulPayload<PatientV2ReviewResponse>(response, 'Não foi possível concluir a análise do paciente.');
};

export const savePatientV2RegulatoryDecision = async (
  patientId: string | number,
  payload: PatientV2RegulatoryDecisionPayload
): Promise<PatientV2RegulatoryDecisionSaveResponse> => {
  const response: any = await doPost(`/patientsv2/${encodeURIComponent(String(patientId))}/regulatory-decision`, payload);
  if (response?.httpStatus && response.httpStatus !== 201) {
    throw new Error(getErrorMessage(response, 'Não foi possível registrar a decisão regulatória.'));
  }
  return getSuccessfulPayload<PatientV2RegulatoryDecisionSaveResponse>(response, 'Não foi possível registrar a decisão regulatória.');
};

export const resubmitPatientV2 = async (patientId: string | number, payload: PatientV2ResubmitPayload): Promise<PatientV2ReviewResponse> => {
  const response: any = await doPost(`/patientsv2/${encodeURIComponent(String(patientId))}/resubmit`, payload);
  if (response?.httpStatus && response.httpStatus !== 200) {
    throw new Error(getErrorMessage(response, 'Não foi possível atualizar a solicitação.'));
  }
  return getSuccessfulPayload<PatientV2ReviewResponse>(response, 'Não foi possível atualizar a solicitação.');
};

export const createPatientV2TriageNote = async (patientId: string | number, payload: PatientV2TriageNotePayload): Promise<PatientV2TriageItemResponse> => {
  const response: any = await doPost(`/patientsv2/${encodeURIComponent(String(patientId))}/triage/notes`, payload);
  if (response?.httpStatus && response.httpStatus !== 201) {
    throw new Error(getErrorMessage(response, 'Não foi possível registrar a anotação na triagem.'));
  }
  return getSuccessfulPayload<PatientV2TriageItemResponse>(response, 'Não foi possível registrar a anotação na triagem.');
};

export const savePatientV2ClinicalInformation = async (patientId: string | number, payload: PatientV2ClinicalInformationPayload): Promise<PatientV2ClinicalInformationPayload> => {
  const response: any = await doPost(`/patientsv2/${encodeURIComponent(String(patientId))}/clinical-information`, payload);
  return getSuccessfulPayload<PatientV2ClinicalInformationPayload>(response, 'Não foi possível salvar as informações clínicas.');
};

export const getMetrics = async (): Promise<PatientsV2MetricsResponse> => {
  const response: any = await doGet(`/patientsv2/metrics`);
  return getSuccessfulPayload<PatientsV2MetricsResponse>(response, 'Não foi possível carregar os pacientes.');
};
